import {
  AccordionDetails,
  Avatar,
  Box,
  Drawer,
  MenuList,
  Popover,
  Stack,
  styled,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { theme } from "../../theme";

const TOP_NAV_HEIGHT = 64;
const NAVBOX_PADDING = "16px";

const SIDE_BAR_OPEN = 280;
const SIDE_BAR_COLLAPSED = 100;

const AVATAR_SIZE = "40px";

const COLAPSE_BUTTON_SIZE = 36;
const COLAPSE_BUTTON_FONT_SIZE = "20px";

const LOGO_PADDING = "32px";

const ICON_SIZE = 20;

const ITEMS_PADDING = "10px";
const ITEMS_HEIGHT = "36px";
const ITEMS_FONT_SIZE = "0.9rem";
const ITEMS_FONT_WEIGHT = "600";
const ITEMS_LINE_HEIGHT = "36px";
const ITEMS_ICON_TEXT_GAP = "18px"

const EXPAND_ICON_SIZE = "16px"

const SUBITEMS_PADDING = "8px";

export const TopNavStyles = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  backdropFilter: "blur(6px)",
  backgroundColor: alpha(theme.palette.background.default, 0.8),
  position: "sticky",
  left: collapsed ? SIDE_BAR_COLLAPSED : SIDE_BAR_OPEN,
  top: 0,
  width: collapsed ? "calc(100% - 80px)" : "calc(100% - 280px)",
  zIndex: theme.zIndex.appBar,
  transition: "all 0.3s ease",
  [theme.breakpoints.down("lg")]: {
    left: 0,
    width: "100%",
  },
}));

export const NavStackStyles = styled(Stack)({
  minHeight: TOP_NAV_HEIGHT,
  padding: "0 32px",
});

export const AvatarStyles = styled(Avatar)({
  cursor: "pointer",
  height: AVATAR_SIZE,
  width: AVATAR_SIZE,
  "& > .MuiBox-root": {
    display: "flex",
  },
});

export const SideNavWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  width: collapsed ? SIDE_BAR_COLLAPSED : SIDE_BAR_OPEN,
  flexShrink: 0,
  height: "100vh",
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  transition: "width 0.3s ease",
}));

export const CollapseToggleButton = styled("div")<{ collapsed: boolean }>(
  ({ theme, collapsed }) => ({
    position: "absolute",
    top: "50%",
    width: COLAPSE_BUTTON_SIZE,
    height: COLAPSE_BUTTON_SIZE,
    right: 0,
    transform: "translateX(50%)",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: theme.shadows[3],
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    svg: {
      fontSize: COLAPSE_BUTTON_FONT_SIZE,
    },
  }));

export const LogoWrapper = styled(Box)({
  padding: LOGO_PADDING,
  "& > .MuiBox-root": {
    display: "inline-flex",
    width: "100%",
  },
});


export const NavDrawerStyles = styled(Drawer)<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  "& > .MuiPaper-root": {
    width: collapsed ? SIDE_BAR_COLLAPSED : SIDE_BAR_OPEN,
    overflow: "visible",
    position: "fixed",
    transition: "width 0.3s ease",
    height: "100vh",
  },
  [theme.breakpoints.down("lg")]: {
    zIndex: theme.zIndex.appBar + 100,
  },
}));

export const NavBox = styled(Box)({
  flex: "1 1 auto",
  minHeight: 0,
  padding: NAVBOX_PADDING,
  overflowY: "auto",
  overflowX: "hidden",
  "& > .MuiStack-root": {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },


  // Scrollbar direction
  direction: 'rtl',

  // Sidebar items direction
  '& > *': {
    direction: 'ltr', 
  },
  
  scrollbarGutter: "stable",

  // Firefox Support
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(155, 155, 155, 0.25) transparent',
  backgroundColor: "transparent",
});

export const LinkWrapper = ({
  children,
  isActive,
  onClick,
  sx,
}: {
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  sx?: any;
}) => {
  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        pl: 2,
        pr: 2,
        py: 0.75,
        width: "100%",
        height: ITEMS_HEIGHT,
        padding: ITEMS_PADDING,
        borderRadius: 1,
        boxSizing: "border-box",
        cursor: "pointer",
        
        svg: {
          width: ICON_SIZE,
          height: ICON_SIZE,
          path: {
            fill: isActive
              ? theme.palette.primary.main + "!important"
              : theme.palette.backgroundColor.main + "!important",
          },
        },
        div: {
          color: isActive
            ? theme.palette.primary.main
            : theme.palette.backgroundColor.main,
          fontFamily: theme.typography.fontFamily,
          fontSize: ITEMS_FONT_SIZE,
          fontWeight: ITEMS_FONT_WEIGHT,
          lineHeight: ITEMS_LINE_HEIGHT,

          whiteSpace: "nowrap",
          textDecoration: "none",
          gap: ITEMS_ICON_TEXT_GAP,
        },
        "&:hover": {
          color: theme.palette.primary.main,
          svg: { path: { fill: theme.palette.primary.main + "!important" } },
          div: { color: theme.palette.primary.main + "!important" },
          ".MuiTypography-root": { color: theme.palette.primary.main + "!important" },
        },
        ...sx,
      })}
    >
      {children}
    </Box>
  );
};

export const LayoutWithNavBarStyles = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: theme.palette.backgroundColor.secondary,
}));

export const LayoutBox = styled(Box)({
  margin: "40px",
});

export const AccountPopoverStyles = styled(Popover)({
  "& > .MuiPaper-root": {
    width: "200px",
    "& > .MuiBox-root": {
      padding: "12px 16px",
    },
  },
});

export const ContactPopoverStyles = styled(Popover)({
  "& > .MuiPaper-root": {
    width: "250px",
    "& > .MuiBox-root": {
      padding: "12px 16px",
    },
  },
});

export const MenuListStyles = styled(MenuList)({
  padding: "8px",
  "& > *": {
    borderRadius: "1",
  },
});

export const ComplexLinkBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ isActive?: boolean; collapsed?: boolean }>(({ theme, isActive, collapsed }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: ITEMS_ICON_TEXT_GAP,
  height: ITEMS_HEIGHT,
  width: "100%",
  transition: "width 0.3s ease",

  svg: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    flexShrink: 0,
    path: {
      fill: isActive
        ? `${theme.palette.primary.main}!important`
        : `${theme.palette.backgroundColor.main}!important`,
    },
  },
  "&:hover": {
    color: theme.palette.primary.main,
    svg: {
      path: { fill: `${theme.palette.primary.main}!important` },
    },
    ".MuiTypography-root, div": {
      color: `${theme.palette.primary.main}!important`,
    },
  }
}));

export const AccordionDetailsMain = styled(AccordionDetails)({
  paddingRight: "0!important",
  minHeight: "36px",
});

export const LayoutRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  transition: "padding-left 0.3s ease",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: collapsed ? SIDE_BAR_COLLAPSED : SIDE_BAR_OPEN,
  },
}));

export const LayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

export const ComplexSideBarLinkUI = ({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) => {
  return (
    <Box
      sx={{
        ".MuiPaper-root": {
          background: "transparent",
          boxShadow: "none",
        },
        ".MuiButtonBase-root": {
          minHeight: "0!important",
          height: ITEMS_HEIGHT,
          padding: ITEMS_PADDING,
        },
        ".MuiAccordionSummary-content": {
          margin: "0!important",
          height: ITEMS_HEIGHT,
          alignItems: "center",
          "& .MuiBox-root": {
            svg: {
              width: ICON_SIZE,
              height: ICON_SIZE,
            },
            ".MuiTypography-root": {
              fontWeight: ITEMS_FONT_WEIGHT,
              fontSize: ITEMS_FONT_SIZE,
              color: (theme) => theme.palette.backgroundColor.main,
            },
          },
        },
        ".MuiAccordionDetails-root .MuiBox-root:hover": {
          color: theme.palette.primary.main,
          "svg path": { fill: `${theme.palette.primary.main}!important` },
          "div, .MuiTypography-root": { color: `${theme.palette.primary.main}!important` },
        },
        ".MuiAccordionSummary-expandIconWrapper": {
          svg: {
            width: EXPAND_ICON_SIZE,
            height: EXPAND_ICON_SIZE,
            path: {
              fill: (theme) => theme.palette.backgroundColor.main,
            },
          },
        },
        ".MuiAccordionDetails-root": {
          padding: SUBITEMS_PADDING,
        },
      }}
    >
      {children}
    </Box>
  );
};